import {RunState} from '@wandb/weave/components/RunState';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import {Timestamp} from '@wandb/weave/components/Timestamp';
import React, {useMemo, useState} from 'react';

import {
  FetchProjectTriggerExecutionsQueryResult,
  FetchProjectTriggersQueryResult,
  TriggerExecution,
} from '../../../../generated/graphql';
import {Analytics} from '../../../../services/analytics';
// @todo: Migrate to 'BasicTable' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Table} from '../../Table';
import {EventTypeChip} from '../EventTypeChip';
import {TriggerName} from '../TriggersTable/TriggerName';
import {
  getActionDisplayName,
  getActionGQLTypename,
  getTriggersDetails,
} from '../TriggersTable/utilData';
import {isSameTrigger} from './utilData';
import {ViewTriggerExecutionDrawer} from './ViewTriggerExecutionDrawer';

type TriggerExecutionsTableProps = {
  projectTriggersQuery: FetchProjectTriggersQueryResult;
  projectTriggerExecutionsQuery: FetchProjectTriggerExecutionsQueryResult;
};

export const TriggerExecutionsTable = ({
  projectTriggersQuery,
  projectTriggerExecutionsQuery,
}: TriggerExecutionsTableProps) => {
  const currentTriggersByID = useMemo(
    () =>
      new Map(
        getTriggersDetails(projectTriggersQuery, undefined)?.map(trigger => [
          trigger.id,
          trigger,
        ])
      ),
    [projectTriggersQuery]
  );

  // Extract the trigger execution records
  const {data, loading, fetchMore} = projectTriggerExecutionsQuery;
  const triggerExecutions = data?.project?.triggerExecutions;
  const pageInfo = triggerExecutions?.pageInfo;

  // Handlers for drawer state
  const [selectedExecution, setSelectedExecution] =
    useState<TriggerExecution | null>(null);

  const rows = useMemo(() => {
    return (triggerExecutions?.edges ?? [])
      .map(edge => {
        const execution = edge.node as TriggerExecution;

        // The GraphQL Trigger/TriggerExecution and related schemas may have evolved since execution time.
        // We hope not, but we're going to err on the side of reasonable defensiveness here.  To check if the trigger is unchanged
        // (i.e. executed trigger == current trigger) we'll intentionally start with coarse heuristics and update this as we go.
        //
        // Note: If triggerExecution.triggerID is null, the trigger no longer exists.
        const currentTrigger = execution.triggerID
          ? currentTriggersByID.get(execution.triggerID)
          : undefined;

        // Note that for our purposes, the code here treats "deleted" vs "edited" triggers as mutually exclusive states.
        const triggerWasDeleted = currentTrigger == null;
        const triggerWasEdited =
          currentTrigger != null && !isSameTrigger(execution, currentTrigger);

        return {
          row: {
            id: execution.id,
            createdAt: execution.createdAt, // Used for default initial sort
            triggerName: (
              <Tailwind style={{height: '100%', width: '100%'}}>
                <div className="flex h-full flex-col items-start justify-center">
                  {triggerWasDeleted ? (
                    // Display the trigger name, but grayed out and without a link, as it no longer exists
                    <div className="text-base font-semibold text-moon-500">
                      {execution.triggerName}
                    </div>
                  ) : (
                    <TriggerName
                      trigger={currentTrigger}
                      // Display the trigger name as recorded at execution time.
                      // This will also avoid confusing discrepancies when searching for a trigger execution by name,
                      // as the search string will be matched against the executed trigger name.
                      displayedName={execution.triggerName}
                      // Don't display the trigger description, it just adds noise
                      displayedDescription={''}
                    />
                  )}
                </div>
              </Tailwind>
            ),
            // Product/design decision: Triggered timestamps will be displayed in table as a relative duration from the current time.
            triggered: (
              <Tailwind style={{height: '100%', width: '100%'}}>
                <div className="flex h-full flex-col items-start justify-center">
                  <button
                    className="cursor-pointer border-0 bg-transparent p-0 font-semibold text-teal-600 hover:text-teal-500"
                    onClick={() => {
                      Analytics.track('Automation Execution History', {
                        type: 'click_execution_timestamp',
                        triggerExecutionId: execution.id,
                        triggerId: execution.triggerID ?? undefined,
                      });
                      setSelectedExecution(execution);
                    }}>
                    <Timestamp value={execution.createdAt} format="relative" />
                  </button>
                </div>
              </Tailwind>
            ),
            event: (
              <Tailwind style={{height: '100%', width: '100%'}}>
                <div className="flex h-full flex-col items-start justify-center">
                  {execution.event?.eventType ? (
                    <EventTypeChip eventType={execution.event?.eventType} />
                  ) : (
                    <div className="text-moon-500">Unknown</div>
                  )}
                </div>
              </Tailwind>
            ),
            action: (
              <Tailwind style={{height: '100%', width: '100%'}}>
                <div className="flex h-full flex-col items-start justify-center">
                  {execution.action?.actionType ? (
                    getActionDisplayName(
                      getActionGQLTypename(execution.action.actionType)
                    )
                  ) : (
                    <div className="text-moon-500">Unknown</div>
                  )}
                </div>
              </Tailwind>
            ),
            status: (
              // Reuse the RunState component for consistency with existing UI elsewhere.
              <Tailwind style={{height: '100%', width: '100%'}}>
                <div className="flex h-full flex-col items-start justify-center">
                  <RunState value={execution.state} />
                </div>
              </Tailwind>
            ),
            executionState: execution.state,
          },
          searchString: execution.triggerName,
        };
      })
      .filter(r => r.row.id != null);
  }, [triggerExecutions, currentTriggersByID]);

  const columns = [
    {
      accessor: 'createdAt',
      show: false,
    },
    {
      Header: 'AUTOMATION',
      accessor: 'triggerName',
      resizable: true,
    },
    {
      Header: 'TRIGGERED',
      accessor: 'triggered',
      resizable: true,
    },
    {
      Header: 'EVENT',
      accessor: 'event',
      resizable: true,
    },
    {
      Header: 'ACTION',
      accessor: 'action',
      resizable: true,
    },
    {
      Header: 'STATUS',
      accessor: 'status',
      width: 110,
      minWidth: 110,
      resizable: true,
    },
  ];

  const handleFetchNextPage = () => {
    if (pageInfo?.hasNextPage && pageInfo?.endCursor) {
      fetchMore({
        variables: {
          after: pageInfo.endCursor,
        },
        updateQuery: (prev, {fetchMoreResult}) => {
          if (!fetchMoreResult?.project || !prev.project) {
            return prev;
          }
          return {
            ...prev,
            project: {
              ...prev.project,
              triggerExecutions: {
                ...fetchMoreResult.project.triggerExecutions,
                edges: [
                  ...prev.project.triggerExecutions.edges,
                  ...fetchMoreResult.project.triggerExecutions.edges,
                ],
              },
            },
          };
        },
      });
    }
  };

  return (
    <>
      <Tailwind style={{height: '100%', width: '100%'}}>
        <div className="mb-16 text-right text-moon-500">
          Automation executions are shown for up to 6 months.
        </div>
      </Tailwind>
      <Table
        data={rows}
        loading={loading}
        columns={columns}
        pageSize={30}
        noPaging={false}
        hasNextPage={pageInfo?.hasNextPage ?? false}
        onFetchNextPage={handleFetchNextPage}
        defaultSorted={[{id: 'createdAt', desc: true}]}
      />

      <ViewTriggerExecutionDrawer
        execution={selectedExecution ?? undefined}
        currentTrigger={
          selectedExecution?.triggerID
            ? currentTriggersByID.get(selectedExecution.triggerID)
            : undefined
        }
        open={selectedExecution != null}
        closeDrawer={() => {
          Analytics.track('Automation Execution History', {
            type: 'click_close_execution_details',
            triggerExecutionId: selectedExecution?.id ?? undefined,
          });
          setSelectedExecution(null);
        }}
      />
    </>
  );
};
